import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useHotelContext } from "@/contexts/HotelContext";
import { useCheckinGate, GateWrap } from "@/hooks/useCheckinGate";
import { PageHeader, Card, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Plus, Calendar, CheckCheck, X as XIcon, LogIn, User, BedDouble, IndianRupee, FileText, Pencil, ChevronRight, ChevronsLeft, Printer, Ticket, Users } from "lucide-react";
import { SwipeableCard } from "@/components/ui/swipeable-card";
import { ReservationSourceInput, fetchReservationSources, isOtaSource } from "@/components/pms/ReservationSourceInput";
import { MultiRoomSelector, emptyRoomLine } from "@/components/pms/MultiRoomSelector";
import { DatePicker } from "@/components/ui/date-picker";
import GovtIdUploader from "@/components/GovtIdUploader";
import { FormField, errorRing } from "@/components/form/FormField";
import { CollapsibleSection } from "@/components/form/CollapsibleSection";
import { useFormValidation } from "@/hooks/useFormValidation";
import { required, email as emailV, phone as phoneV } from "@/lib/validators";
import { RateInputWithTax, nightsBetween } from "@/components/pms/RateInput";
import { AdvancePaymentInput, advanceTotal, advancePayload, validateAdvances } from "@/components/pms/AdvancePaymentInput";
import { CheckInDialog } from "@/components/pms/CheckInDialog";
import { Wallet } from "lucide-react";
import { fmtINR, openPdf, reservationRegCardUrl, voucherUrl } from "@/lib/documents";
import { requestRegCard } from "@/components/documents/RegCardOptions";
import { DownloadPdfButton } from "@/components/documents/DownloadPdfButton";
import { AssignRoomButton } from "@/components/pms/AssignRoomButton";

const initialForm = {
  salutation: "", first_name: "", last_name: "", email: "", primary_phone: "",
  arrival_date: "", departure_date: "",
  adults: 1, children: 0, requested_room_type_id: "", assigned_room_id: "",
  entered_rate: "", tax_inclusive_input: false, rate_plan_id: "",
  reservation_source: "", reservation_source_ref: "",
  booking_source: "direct", booking_type: "individual", notes: "",
  breakfast_included: false,
  nationality: "", govt_id_document_url: "",
};

function fmt(d) { return (d || "").slice(0, 16).replace("T", " "); }
const multiTotal = (lines, group, single) => (lines.length > 1 ? (group?.grand || 0) : (single?.grand || 0));

/** Return YYYY-MM-DD strings for every breakfast the guest is entitled to.
 * BB convention: breakfast is served the morning AFTER each night, so a
 * guest checking in 25 Jul and out 26 Jul gets ONE breakfast — on 26 Jul.
 * Range: (arrival_date + 1 day) … departure_date (inclusive of departure). */
function breakfastDates(arrivalIso, departureIso) {
  if (!arrivalIso || !departureIso) return [];
  const a = new Date(arrivalIso);
  const d = new Date(departureIso);
  if (isNaN(a) || isNaN(d) || d <= a) return [];
  const out = [];
  const cur = new Date(a.getFullYear(), a.getMonth(), a.getDate() + 1);
  const end = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  while (cur <= end) {
    out.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

export default function Reservations() {
  const navigate = useNavigate();
  const location = useLocation();
  const { ctx } = useHotelContext();
  // The reservation is always created inside the currently-active hotel — no
  // per-form hotel selector. The hotel_id comes straight from HotelContext.
  const activeHotelId = ctx?.hotel_id || "";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [roomTypes, setRoomTypes] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [taxConfigured, setTaxConfigured] = useState(true);
  const [breakdown, setBreakdown] = useState(null);
  const [roomLines, setRoomLines] = useState([emptyRoomLine()]);
  const [groupSummary, setGroupSummary] = useState(null);
  const [advances, setAdvances] = useState([]);
  const grandTotal = multiTotal(roomLines, groupSummary, breakdown);
  const advancePaid = advanceTotal(advances);
  const multi = roomLines.length > 1;
  const changeLines = (ls) => { setRoomLines(ls); if (ls.length === 1) setForm(f => ({ ...f, assigned_room_id: ls[0].room_id || "", entered_rate: ls[0].entered_rate, tax_inclusive_input: !!ls[0].tax_inclusive_input, rate_plan_id: ls[0].rate_plan_id || "" })); };
  const setRoomCount = (n) => {
    if (n <= 1) { changeLines([roomLines[0] || emptyRoomLine()]); return; }
    const first = roomLines.length > 1 ? roomLines[0] : { ...emptyRoomLine(), room_type_id: form.requested_room_type_id || availableRooms.find(r => r.room_id === form.assigned_room_id)?.room_type_id || "", entered_rate: form.entered_rate, tax_inclusive_input: form.tax_inclusive_input, rate_plan_id: form.rate_plan_id, pax_adults: form.adults, pax_children: form.children };
    const rest = roomLines.slice(1, n);
    setRoomLines([first, ...rest, ...Array.from({ length: Math.max(0, n - 1 - rest.length) }, emptyRoomLine)]);
  };
  const nights = nightsBetween(form.arrival_date, form.departure_date);
  const roomOptions = useMemo(() => availableRooms.map(r => ({ id: r.room_id, room_type_id: r.room_type_id, label: `${r.room_number}${(r.room_type_name || roomTypes.find(rt => rt.id === r.room_type_id)?.name) ? ` — ${r.room_type_name || roomTypes.find(rt => rt.id === r.room_type_id)?.name}` : ""}` })), [availableRooms, roomTypes]);
  // OTA-PHONE · phone is optional when the booking comes from an OTA channel (guest contact lives with the OTA)
  const isOta = isOtaSource(form.reservation_source);
  const v = useFormValidation({
    first_name: [required("First name is required")],
    primary_phone: isOta ? [phoneV()] : [required("Phone is required"), phoneV()],
    email: [emailV()],
    arrival_date: [required("Arrival date is required")],
    departure_date: [required("Departure date is required")],
  });

  const [sourceFilter, setSourceFilter] = useState("");
  const [sourceOptions, setSourceOptions] = useState([]);
  useEffect(() => { fetchReservationSources().then(setSourceOptions).catch(() => {}); }, []);
  // Timeline "+ New Reservation": /pms/reservations?new=1&room_id=&arrival=&departure=
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.get("new") !== "1") return;
    v.reset(); setAdvances([]);
    setForm({ ...initialForm, arrival_date: sp.get("arrival") || "", departure_date: sp.get("departure") || "", assigned_room_id: sp.get("room_id") || "" });
    setOpen(true);
    navigate("/pms/reservations", { replace: true });
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps
  const load = async () => {
    const p = new URLSearchParams({ page: "1", page_size: "50" });
    if (status) p.set("status", status);
    if (q) p.set("q", q);
    if (sourceFilter) p.set("reservation_source", sourceFilter);
    try {
      const { data } = await api.get(`/pms/reservations/?${p}`);
      setItems(data.items); setTotal(data.total);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, [status, q, sourceFilter]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/master/room-types/?page_size=100");
        setRoomTypes(r.data.items);
      } catch (e) { setRoomTypes([]); toast.error(`Room types could not be loaded: ${formatApiError(e)}`); }
    })();
  }, []);

  // Fetch available rooms once we know the active hotel — scopes the "Assign
  // Room" dropdown to the reservation's hotel automatically.
  useEffect(() => {
    if (!activeHotelId) { setAvailableRooms([]); return; }
    (async () => {
      try {
        const { data } = await api.get(
          `/inventory/available?limit=200&include_dirty=true&hotel_id=${activeHotelId}`);
        setAvailableRooms(data.items || []);
      } catch { setAvailableRooms([]); }
    })();
  }, [activeHotelId]);

  const create = async () => {
    const valid = v.validateAll(form);
    if (!taxConfigured) { toast.error("Tax rates are not configured for this hotel — set them in Billing & Tax settings first"); return; }
    if (!multi && form.entered_rate !== "" && form.entered_rate != null && !(Number(form.entered_rate) > 0)) { toast.error("Nightly rate must be greater than zero"); return; }
    if (multi && roomLines.some(l => !(l.room_id || l.room_type_id) || !(Number(l.entered_rate) > 0))) { toast.error("Every room line needs a room type (or room) and a nightly rate"); return; }
    const advErr = validateAdvances(advances);
    if (advErr) { toast.error(advErr); return; }
    if (!activeHotelId) {
      toast.error("No active hotel — pick a hotel from the top bar and try again");
      return;
    }
    if (!valid) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    setSubmitting(true);
    try {
      // Step 1 — resolve guest: create a new one, OR reuse an existing match
      //          transparently so we never write duplicate profiles.
      let guest_id;
      try {
        const { data: g } = await api.post("/pms/guests/", {
          salutation: form.salutation || null,
          first_name: form.first_name.trim(),
          last_name: (form.last_name || "").trim() || null,
          email: (form.email || "").trim() || null,
          primary_phone: (form.primary_phone || "").trim() || null,
          hotel_id: activeHotelId,
          nationality: form.nationality || null,
          govt_id_document_url: form.govt_id_document_url || null,
        });
        guest_id = g.id;
      } catch (e) {
        // 409 → existing guest matched. Reuse the first match rather than
        // asking the operator to reconcile — the reservation flow is
        // optimised for speed at the counter.
        const dups = e?.response?.data?.detail?.matches;
        if (e?.response?.status === 409 && Array.isArray(dups) && dups.length) {
          guest_id = dups[0].id;
          toast.info(`Existing guest matched — using ${dups[0].first_name || ""} ${dups[0].last_name || ""}`.trim());
        } else {
          throw e;
        }
      }

      // Step 2 — build reservation payload
      const payload = {
        guest_id,
        hotel_id: activeHotelId,
        arrival_date: form.arrival_date,
        departure_date: form.departure_date,
        adults: form.adults,
        children: form.children,
        booking_source: form.booking_source,
        booking_type: form.booking_type,
      };
      if (multi) payload.rooms = roomLines.map(l => ({ room_id: l.room_id || null, room_type_id: l.room_type_id || null, pax_adults: Number(l.pax_adults) || 1, pax_children: Number(l.pax_children) || 0, entered_rate: Number(l.entered_rate), tax_inclusive_input: !!l.tax_inclusive_input, rate_plan_id: l.rate_plan_id || null, extra_bed: !!l.extra_bed, notes: l.notes || null, guest_name: (l.guest_name || "").trim() || null }));
      else if (form.assigned_room_id) payload.assigned_room_id = form.assigned_room_id;
      if (form.rate_plan_id) payload.rate_plan_id = form.rate_plan_id;
      if (form.entered_rate !== "" && form.entered_rate != null) { payload.entered_rate = Number(form.entered_rate); payload.tax_inclusive_input = !!form.tax_inclusive_input; }
      if (form.requested_room_type_id) payload.requested_room_type_id = form.requested_room_type_id;
      if (form.notes) payload.notes = form.notes;
      if (form.reservation_source?.trim()) { payload.reservation_source = form.reservation_source.trim(); if (form.reservation_source_ref?.trim()) payload.reservation_source_ref = form.reservation_source_ref.trim(); }
      if (form.nationality) payload.nationality = form.nationality;
      if (form.govt_id_document_url) payload.govt_id_document_url = form.govt_id_document_url;
      if (advances.length) Object.assign(payload, advancePayload(advances, grandTotal));
      // Translate the "Breakfast included?" toggle into stay_benefits.meal_plan
      // so the F&B Breakfast Register picks up eligible guests automatically.
      if (form.breakfast_included) {
        const dates = breakfastDates(form.arrival_date, form.departure_date);
        payload.stay_benefits = {
          meal_plan: {
            kind: "bb",
            includes_breakfast: true,
            includes_lunch: false,
            includes_dinner: false,
            adults_covered: Number(form.adults) || 0,
            children_covered: Number(form.children) || 0,
            valid_breakfast_dates: dates,
            valid_lunch_dates: [],
            valid_dinner_dates: [],
          },
        };
      }
      const { data: created } = await api.post("/pms/reservations/", payload);
      toast.success(form.breakfast_included
        ? "Reservation created — breakfast added to F&B register"
        : created?.advance_paid_total ? `Reservation created — advance ${fmtINR(created.advance_paid_total)} recorded` : "Reservation created");
      setOpen(false); setForm(initialForm); setRoomLines([emptyRoomLine()]); setAdvances([]); load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSubmitting(false);
  };

  const cancel = async () => {
    if (!cancelTarget) return;
    try {
      await api.post(`/pms/reservations/${cancelTarget.id}/cancel`, { reason: cancelReason || "Cancelled" });
      toast.success("Cancelled"); setCancelTarget(null); setCancelReason(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [checkInId, setCheckInId] = useState(null);
  const gate = useCheckinGate();
  const convert = async (r) => {
    if (gate.isBlocked(r.arrival_date)) { toast.error(gate.reason(r.arrival_date)); return; }
    // GROUP-CHECKIN · multi-room bookings check in from the Group Check-In tab (all rooms or one at a time)
    if (r.is_multi_room && (r.rooms || []).length > 1) { navigate(`/pms/reservations/${r.id}?tab=group`); return; }
    // Reservations without an assigned room can't check in — the Assign Room button sits in the same row.
    if (!r.assigned_room_id) {
      toast.error("Assign a room before checking in", { description: "Use the Assign Room button on this booking." });
      return;
    }
    setCheckInId(r.id);
  };

  return (
    <div className="space-y-6" data-testid="reservations-page">
      <PageHeader
        title="Reservations"
        subtitle={`${total} bookings · Pre-arrival operations`}
        action={
          <Button onClick={() => { v.reset(); setForm(initialForm); setAdvances([]); setOpen(true); }} data-testid="create-reservation-button">
            <Plus className="w-4 h-4 mr-2"/>New Reservation
          </Button>
        }
      />

      <Card className="p-3">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <Input placeholder="Search code / guest" aria-label="Search reservations by code or guest" value={q} onChange={(e) => setQ(e.target.value)} data-testid="reservations-search"/>
          <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-status" aria-label="Filter by status"><SelectValue placeholder="Status"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="checked_in">Checked In</SelectItem>
              <SelectItem value="checked_out" data-testid="res-filter-checked-out">Checked Out</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="no_show">No Show</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sourceFilter || "all"} onValueChange={(v) => setSourceFilter(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-source" aria-label="Filter by reservation source"><SelectValue placeholder="Source"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              {sourceOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Mobile — card layout */}
      <div className="md:hidden space-y-3" data-testid="reservations-cards">
        {items.length > 0 && <div className="text-[11px] text-hocp-textMuted flex items-center gap-1 px-1"><ChevronsLeft size={14} strokeWidth={1.5} /> Swipe a card left for quick actions</div>}
        {items.map(r => {
          const tone = r.status === "confirmed" ? "info" : r.status === "checked_in" ? "success" : r.status === "cancelled" ? "error" : r.status === "no_show" ? "warning" : "neutral";
          const statusLabel = r.status === "checked_out" ? "Checked Out" : r.status === "checked_in" && r.partial_checkin ? "Partially checked in" : r.status;
          const openRes = r.status === "confirmed" || r.status === "pending";
          const actions = [
            { key: "edit", label: "Edit", icon: Pencil, tone: "edit", onClick: () => navigate(`/pms/reservations/${r.id}`), testid: `swipe-edit-${r.code}` },
            ...(!["cancelled", "no_show"].includes(r.status) ? [{ key: "regcard", label: "Reg Card", icon: Printer, tone: "edit", onClick: () => requestRegCard({ url: reservationRegCardUrl(r.id), filename: `RegistrationCard-${r.code}.pdf` }), testid: `swipe-regcard-${r.code}` }] : []),
            ...(openRes ? [
              { key: "checkin", label: "Check-in", icon: LogIn, tone: "primary", onClick: () => convert(r), testid: `swipe-checkin-${r.code}`, disabled: gate.isBlocked(r.arrival_date) },
              { key: "cancel", label: "Cancel", icon: XIcon, tone: "danger", onClick: () => setCancelTarget(r), testid: `swipe-cancel-${r.code}` },
            ] : []),
          ];
          return (
            <SwipeableCard key={r.id} actions={actions} testid={`reservation-card-${r.code}`} className="border border-hocp-border shadow-hocp-sm">
              <div className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <Link to={`/pms/reservations/${r.id}`} className="text-[15px] font-bold text-hocp-textPrimary hover:underline">{r.guest_name || r.guest_id}</Link>
                  <Badge tone={tone}>{statusLabel}</Badge>
                </div>
                <div className="text-[14px] text-hocp-textMuted"><span className="mono text-[12.5px]">{fmt(r.arrival_date)}</span> → <span className="mono text-[12.5px]">{fmt(r.departure_date)}</span> · {r.adults} adult{r.adults === 1 ? "" : "s"}{r.children ? `, ${r.children} kids` : ""}</div>
                <div className="flex items-center gap-2 text-[13px]" data-testid={`room-cell-mobile-${r.code}`}>
                  <span className="text-hocp-textMuted">Room:</span>
                  {r.is_multi_room && (r.rooms || []).length > 1 ? <span className="mono text-hocp-textPrimary">{r.rooms.map(l => l.room_number).filter(Boolean).join(", ")}</span> : <AssignRoomButton reservation={r} onAssigned={load} testIdPrefix="m-" />}
                  {!r.room_number && !(r.rooms || []).length && !["confirmed", "pending"].includes(r.status) && <span className="text-hocp-textMuted">—</span>}
                </div>
                <div className="flex items-center justify-between gap-2 pt-1">
                  <div className="mono text-[12px] text-hocp-deepTeal font-semibold">{r.code}{r.reservation_source ? <span className="ml-2 font-sans font-normal text-hocp-textMuted">· {r.reservation_source}</span> : null}</div>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/pms/reservations/${r.id}`)} data-testid={`view-${r.code}`}>View <ChevronRight className="w-3.5 h-3.5 ml-0.5" /></Button>
                </div>
              </div>
            </SwipeableCard>
          );
        })}
        {!items.length && <Card className="p-8 text-center text-hocp-textTertiary">No reservations</Card>}
      </div>

      <Card className="overflow-hidden hidden md:block">
        <table className="w-full" data-testid="reservations-table">
          <thead className="bg-hocp-softIce border-b border-hocp-border">
            <tr>
              {["Code", "Guest", "Arrival", "Departure", "Room", "Adults/Kids", "Status", "Actions"].map(h => (
                <th key={h} className={`px-4 py-3 text-[12px] uppercase tracking-[0.08em] font-semibold text-hocp-deepTeal text-left ${h === "Adults/Kids" ? "hidden lg:table-cell" : ""}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map(r => (
              <tr key={r.id} data-testid={`reservation-row-${r.code}`} className="border-b border-hocp-borderSubtle last:border-0 hover:bg-hocp-tableHover transition-colors">
                <td className="px-4 py-3 mono">
                  <Link to={`/pms/reservations/${r.id}`} className="font-semibold text-hocp-orangeStrong hover:text-hocp-navy hover:underline" data-testid={`open-${r.code}`}>{r.code}</Link>
                </td>
                <td className="px-4 py-3 text-hocp-textPrimary font-medium">
                  <Link to={`/pms/reservations/${r.id}`} className="hover:underline">{r.guest_name || r.guest_id}</Link>
                </td>
                <td className="px-4 py-3 mono text-hocp-textSecondary text-[12.5px]">{fmt(r.arrival_date)}</td>
                <td className="px-4 py-3 mono text-hocp-textSecondary text-[12.5px]">{fmt(r.departure_date)}</td>
                <td className="px-4 py-3 text-[12.5px]" data-testid={`room-cell-${r.code}`}>
                  {r.is_multi_room && (r.rooms || []).length > 1
                    ? <span className="mono text-hocp-textPrimary">{r.rooms.map(l => l.room_number).filter(Boolean).join(", ")}</span>
                    : <AssignRoomButton reservation={r} onAssigned={load} /> }
                  {!r.room_number && !(r.rooms || []).length && !["confirmed", "pending"].includes(r.status) && <span className="text-hocp-textMuted">—</span>}
                </td>
                <td className="px-4 py-3 mono text-hocp-textPrimary text-[12.5px] hidden lg:table-cell">{r.adults}/{r.children}</td>
                <td className="px-4 py-3">
                  <Badge tone={
                    r.status === "confirmed" ? "info" :
                    r.status === "checked_in" ? "success" :
                    r.status === "cancelled" ? "error" :
                    r.status === "no_show" ? "warning" : "neutral"
                  } data-testid={`res-status-${r.id}`}>{r.status === "checked_out" ? "Checked Out" : r.status === "checked_in" && r.partial_checkin ? "Partially checked in" : r.status}</Badge>
                  {r.is_multi_room && (r.rooms || []).length > 1 && <Badge tone="neutral" className="ml-1" data-testid={`res-group-${r.id}`}><Users className="w-3 h-3 mr-1 inline" />{r.rooms.length}</Badge>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-1">
                  {!["cancelled", "no_show"].includes(r.status) && (<>
                    <Button size="sm" variant="outline" title="Print registration card" onClick={() => requestRegCard({ url: reservationRegCardUrl(r.id), filename: `RegistrationCard-${r.code}.pdf` })} data-testid={`regcard-${r.code}`}>
                      <Printer className="w-3 h-3 mr-1"/>Reg Card
                    </Button>
                    <Button size="sm" variant="outline" title="Print booking voucher" onClick={() => openPdf(voucherUrl(r.id), `BookingVoucher-${r.code}.pdf`)} data-testid={`voucher-${r.code}`}>
                      <Ticket className="w-3 h-3 mr-1"/>Voucher
                    </Button>
                    <DownloadPdfButton url={voucherUrl(r.id)} filename={`BookingVoucher-${r.code}.pdf`} label="Download voucher" iconOnly className="!h-8 md:!h-8 !min-h-0 w-8 justify-center px-0" testId={`voucher-download-${r.code}`} />
                  </>)}
                  {(r.status === "confirmed" || r.status === "pending") && (
                    <GateWrap blocked={gate.isBlocked(r.arrival_date)} reason={gate.reason(r.arrival_date)} testid={`convert-gate-${r.code}`}>
                      <Button size="sm" variant="outline" onClick={() => convert(r)} disabled={gate.isBlocked(r.arrival_date)} data-testid={`convert-${r.code}`}>
                        {r.is_multi_room && (r.rooms || []).length > 1 ? <Users className="w-3 h-3 mr-1"/> : <LogIn className="w-3 h-3 mr-1"/>}{r.is_multi_room && (r.rooms || []).length > 1 ? "Group Check-in" : "Check-in"}
                      </Button>
                    </GateWrap>
                  )}
                  {(r.status === "confirmed" || r.status === "pending") && (
                    <Button size="sm" variant="outline" onClick={() => setCancelTarget(r)} data-testid={`cancel-${r.code}`}>
                      <XIcon className="w-3 h-3 mr-1"/>Cancel
                    </Button>
                  )}
                  </div>
                </td>
              </tr>
            ))}
            {!items.length && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-hocp-textTertiary">No reservations</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* Create dialog — fixed header, scrollable body, fixed footer (Total Payable + actions) */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-hocp-bgCard border border-hocp-border md:max-w-[700px] max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden" data-testid="new-reservation-dialog">
          <DialogHeader className="shrink-0 px-4 md:px-6 py-4 border-b border-hocp-border"><DialogTitle>New Reservation</DialogTitle><DialogDescription className="sr-only">Create a new reservation for a guest</DialogDescription></DialogHeader>
          <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-3" style={{ WebkitOverflowScrolling: "touch" }} data-testid="new-reservation-body">
          <CollapsibleSection id="guest" title="Guest Details" icon={User} storageKey="reservation" testid="res-section-guest">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Title" htmlFor="form-salutation" optional labelClassName="text-[11px] mono uppercase">
              <Select value={form.salutation || "__none__"} onValueChange={(val) => setForm({ ...form, salutation: val === "__none__" ? "" : val })}>
                <SelectTrigger id="form-salutation" data-testid="form-salutation"><SelectValue placeholder="Select title"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {["Mr", "Mrs", "Ms", "Miss", "Dr", "Prof", "Mx"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="First Name" htmlFor="form-first-name" required error={v.fieldError("first_name")} labelClassName="text-[11px] mono uppercase">
              <Input id="form-first-name" value={form.first_name}
                      onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                      onBlur={() => v.handleBlur("first_name", form.first_name)}
                      placeholder="e.g. Priya"
                      className={errorRing(v.fieldError("first_name"))}
                      data-testid="form-first-name"/>
            </FormField>
            <FormField label="Last Name" htmlFor="form-last-name" optional labelClassName="text-[11px] mono uppercase">
              <Input id="form-last-name" value={form.last_name}
                      onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                      placeholder="e.g. Sharma"
                      data-testid="form-last-name"/>
            </FormField>
            <FormField label="Phone" htmlFor="form-phone" required={!isOta} optional={isOta} hint={isOta ? "Not required for OTA bookings — the channel holds the guest's contact." : undefined} error={v.fieldError("primary_phone")} labelClassName="text-[11px] mono uppercase">
              <Input id="form-phone" value={form.primary_phone}
                      onChange={(e) => setForm({ ...form, primary_phone: e.target.value })}
                      onBlur={() => v.handleBlur("primary_phone", form.primary_phone)}
                      placeholder="e.g. +91 98xxx xxxxx"
                      className={errorRing(v.fieldError("primary_phone"))}
                      data-testid="form-phone"/>
            </FormField>
            <FormField label="Email" htmlFor="form-email" optional error={v.fieldError("email")} labelClassName="text-[11px] mono uppercase">
              <Input id="form-email" type="email" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      onBlur={() => v.handleBlur("email", form.email)}
                      placeholder="guest@example.com"
                      className={errorRing(v.fieldError("email"))}
                      data-testid="form-email"/>
            </FormField>
            <div>
              <Label className="text-[11px] mono uppercase">Nationality</Label>
              <Input value={form.nationality || ""}
                      onChange={(e) => setForm({ ...form, nationality: e.target.value })}
                      placeholder="e.g. Indian, British, US"
                      data-testid="form-nationality"/>
            </div>
            <div className="col-span-2">
              <Label className="text-[11px] mono uppercase">Govt ID document</Label>
              <GovtIdUploader value={form.govt_id_document_url}
                               onChange={(url) => setForm({ ...form, govt_id_document_url: url })}
                               testid="form-idupload"/>
            </div>
          </div>
          </CollapsibleSection>

          <CollapsibleSection id="stay" title="Stay Details" icon={BedDouble} storageKey="reservation" testid="res-section-stay">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Arrival" htmlFor="form-arrival" required error={v.fieldError("arrival_date")} labelClassName="text-[11px] mono uppercase">
              <DatePicker value={form.arrival_date} onChange={(val) => { setForm({ ...form, arrival_date: val }); v.handleBlur("arrival_date", val); }} data-testid="form-arrival"/>
            </FormField>
            <FormField label="Departure" htmlFor="form-departure" required error={v.fieldError("departure_date")} labelClassName="text-[11px] mono uppercase">
              <DatePicker value={form.departure_date} onChange={(val) => { setForm({ ...form, departure_date: val }); v.handleBlur("departure_date", val); }} minDate={form.arrival_date} data-testid="form-departure"/>
            </FormField>
            <div>
              <Label className="text-[11px] mono uppercase">Nights</Label>
              <Input readOnly value={nights || ""} placeholder="—" className="mono" data-testid="form-nights"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] mono uppercase">Adults</Label>
                <Input type="number" min={1} value={form.adults} onChange={(e) => setForm({ ...form, adults: +e.target.value })} data-testid="form-adults"/>
              </div>
              <div>
                <Label className="text-[11px] mono uppercase">Children</Label>
                <Input type="number" min={0} value={form.children} onChange={(e) => setForm({ ...form, children: +e.target.value })} data-testid="form-children"/>
              </div>
            </div>
            <div>
              <Label className="text-[11px] mono uppercase">Room Type</Label>
              <Select value={form.requested_room_type_id || "none"} onValueChange={(v) => setForm({ ...form, requested_room_type_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="form-room-type"><SelectValue placeholder="(optional)"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {roomTypes.map(rt => <SelectItem key={rt.id} value={rt.id}>{rt.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className={multi ? "col-span-2" : ""}>
              <Label className="text-[11px] mono uppercase">Number of Rooms</Label>
              <div className="flex gap-1 mb-2" data-testid="res-room-count">{[1, 2, 3, 4, 5].map(n => <Button key={n} type="button" size="sm" variant={roomLines.length === n || (n === 5 && roomLines.length > 5) ? "default" : "outline"} className="h-9 min-w-[40px]" onClick={() => setRoomCount(n)} data-testid={`res-room-count-${n}`}>{n}{n === 5 ? "+" : ""}</Button>)}</div>
              {multi ? (
                <MultiRoomSelector value={roomLines} onChange={changeLines} roomOptions={roomOptions} roomTypes={roomTypes} nights={nights} testidPrefix="res-mr" onSummary={setGroupSummary} onConfigured={setTaxConfigured} hideCount />
              ) : (<>
              <Label className="text-[11px] mono uppercase">Assign Room</Label>
              <Select value={form.assigned_room_id || "none"} onValueChange={(v) => setForm({ ...form, assigned_room_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="form-room"><SelectValue placeholder="(optional)"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None (assign later) —</SelectItem>
                  {availableRooms.slice(0, 100).map(r => {
                    const tName = r.room_type_name || roomTypes.find(rt => rt.id === r.room_type_id)?.name;
                    return <SelectItem key={r.room_id} value={r.room_id}>{r.room_number}{tName ? ` · ${tName}` : ""}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
              </>)}
            </div>
            <div className="col-span-2">
              <ReservationSourceInput testidPrefix="res-source" value={{ reservation_source: form.reservation_source, reservation_source_ref: form.reservation_source_ref }}
                onChange={(v) => setForm({ ...form, ...v })} />
            </div>
            <div className="col-span-2 flex items-center justify-between rounded-md border border-hocp-border bg-hocp-bgCard px-3 py-2.5">
              <div>
                <Label className="text-[11px] mono uppercase text-slate-300">Breakfast Included?</Label>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  When Yes, breakfast is added for every night of the stay in the F&amp;B Breakfast Register.
                </div>
              </div>
              <div className="flex gap-1" data-testid="form-breakfast-included">
                <Button
                  type="button"
                  variant={form.breakfast_included ? "default" : "outline"}
                  size="sm"
                  data-testid="form-breakfast-yes"
                  onClick={() => setForm({ ...form, breakfast_included: true })}
                  className="h-8 px-3"
                >Yes</Button>
                <Button
                  type="button"
                  variant={!form.breakfast_included ? "default" : "outline"}
                  size="sm"
                  data-testid="form-breakfast-no"
                  onClick={() => setForm({ ...form, breakfast_included: false })}
                  className="h-8 px-3"
                >No</Button>
              </div>
            </div>
          </div>
          </CollapsibleSection>

          {!multi && <CollapsibleSection id="rate" title="Rate & Charges" icon={IndianRupee} storageKey="reservation" testid="res-section-rate">
            <RateInputWithTax testidPrefix="res-rate" nights={nights} roomTypeId={form.requested_room_type_id}
              value={{ entered_rate: form.entered_rate, tax_inclusive_input: form.tax_inclusive_input, rate_plan_id: form.rate_plan_id }}
              onChange={(v) => setForm({ ...form, ...v })} onConfigured={setTaxConfigured} onBreakdown={setBreakdown} />
          </CollapsibleSection>}

          <CollapsibleSection id="advance" title="Advance Payment" icon={Wallet} storageKey="reservation" defaultOpen={false} summary={advancePaid > 0 ? fmtINR(advancePaid) : undefined} testid="res-section-advance">
            <AdvancePaymentInput value={advances} onChange={setAdvances} grandTotal={grandTotal} nights={nights} testidPrefix="res-adv" />
          </CollapsibleSection>

          <CollapsibleSection id="requests" title="Special Requests" icon={FileText} storageKey="reservation" testid="res-section-requests">
            <div>
              <Label className="text-[11px] mono uppercase">Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Preferences, arrival notes…" data-testid="form-notes"/>
            </div>
          </CollapsibleSection>
          </div>
          <DialogFooter className="shrink-0 px-4 md:px-6 py-4 border-t border-hocp-border max-sm:flex-col max-sm:items-stretch sm:justify-between items-center gap-3">
            <div data-testid="res-total-payable">
              <div className="text-[10px] mono uppercase tracking-wider text-hocp-textMuted">Total Payable</div>
              <div className="text-[18px] font-bold mono leading-tight" data-testid="res-total-payable-amount">{fmtINR(multi ? (groupSummary?.grand || 0) : (breakdown?.grand || 0))}</div>
              <div className="text-[11px] text-hocp-textMuted">{(multi ? groupSummary?.nights : breakdown?.nights) ?? nights ?? 0} night{((multi ? groupSummary?.nights : breakdown?.nights) ?? nights) === 1 ? "" : "s"}{multi ? ` · ${roomLines.length} rooms` : ""} · incl. GST</div>
              {advancePaid > 0 && (
                <div className="text-[11px] text-hocp-deepTeal mt-0.5" data-testid="res-footer-advance">Advance {fmtINR(advancePaid)} · Balance at check-in <span className="mono font-semibold" data-testid="res-footer-balance">{fmtINR(Math.max(0, grandTotal - advancePaid))}</span></div>
              )}
            </div>
            <div className="flex gap-2 max-sm:w-full [&>button]:max-sm:flex-1">
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="cancel-reservation">Cancel</Button>
              <Button onClick={create} disabled={submitting || !taxConfigured} data-testid="submit-reservation">
                {submitting ? "Creating..." : <><CheckCheck className="w-4 h-4 mr-2"/>Create Reservation</>}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <CheckInDialog reservationId={checkInId} open={!!checkInId} onOpenChange={(v) => !v && setCheckInId(null)} onDone={() => load()} />
      <AlertDialog open={!!cancelTarget} onOpenChange={(v) => !v && setCancelTarget(null)}>
        <AlertDialogContent className="bg-hocp-bgCard border border-hocp-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Reservation?</AlertDialogTitle>
            <AlertDialogDescription>
              Reservation <span className="mono">{cancelTarget?.code}</span> will be cancelled. This will release any assigned room.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="mt-2">
            <Label className="text-[11px] mono uppercase" htmlFor="cancel-reason-input">Cancellation reason</Label>
            <Input
              id="cancel-reason-input"
              placeholder="e.g. Guest requested cancellation"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="mt-1"
              data-testid="cancel-reason"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Back</AlertDialogCancel>
            <AlertDialogAction onClick={cancel} data-testid="confirm-cancel">Cancel Reservation</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
